/**
 * Retournement 3D d'une carte (page publique /card/$cardId, spec § 9).
 *
 * L'élève vient de scanner le QR du VERSO : la carte apparaît côté verso, puis
 * se retourne d'elle-même pour révéler le recto (CSS 3D, cf. card-face.css de `@jeraisonne/ui/tool-cards`, chargé avec les faces —
 * aucune vidéo). Cliquer la carte (ou Entrée / Espace) la retourne — et saute
 * l'animation si elle n'a pas encore eu lieu. `prefers-reduced-motion` → recto d'emblée.
 */

import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '@jeraisonne/ui';
import { ScaledCard } from './card-faces';

export type CardSide = 'verso' | 'recto';

/** Délai avant le retournement automatique : le temps de reconnaître le verso scanné. */
export const AUTO_FLIP_DELAY_MS = 2000;

export interface CardFlipProps {
  recto: ReactNode;
  verso: ReactNode;
  /** Appelé une fois quand le recto devient la face affichée. */
  onRevealed?: () => void;
}

export function CardFlip({ recto, verso, onRevealed }: CardFlipProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [side, setSide] = useState<CardSide>(reducedMotion ? 'recto' : 'verso');
  const [autoDone, setAutoDone] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      setSide('recto');
      setAutoDone(true);
      return;
    }
    if (autoDone) return;
    const t = window.setTimeout(() => {
      setSide('recto');
      setAutoDone(true);
    }, AUTO_FLIP_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [reducedMotion, autoDone]);

  useEffect(() => {
    if (side === 'recto') onRevealed?.();
    // onRevealed volontairement hors dépendances : un seul signal par passage au recto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  // Un clic (ou Entrée / Espace) sur la carte la retourne — et saute
  // l'animation automatique si elle n'a pas encore eu lieu.
  const flip = () => {
    setAutoDone(true);
    setSide((s) => (autoDone ? (s === 'recto' ? 'verso' : 'recto') : 'recto'));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      flip();
    }
  };

  // `div role="button"` (pas <button>) : le recto contient lui-même un bouton
  // (flèche « suite ») — des boutons imbriqués seraient du HTML invalide.
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={
        side === 'recto'
          ? 'Retourner la carte : voir le verso'
          : 'Retourner la carte : voir le recto'
      }
      onClick={flip}
      onKeyDown={onKeyDown}
      className="jr-flip focus-visible:ring-primary-400 mx-auto block w-full max-w-[22rem] cursor-pointer rounded-[12.16%/8.71%] outline-none focus-visible:ring-4"
    >
      <div className="jr-flip-inner" data-side={side} data-testid="card-flip">
        <div className="jr-flip-face" aria-hidden={side !== 'verso'}>
          <ScaledCard>{verso}</ScaledCard>
        </div>
        <div className="jr-flip-face jr-flip-face-recto" aria-hidden={side !== 'recto'}>
          <ScaledCard>{recto}</ScaledCard>
        </div>
      </div>
    </div>
  );
}
